import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import i18n from '@/shared/config/i18n/i18n';

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ErrorBoundary]', error, errorInfo);
  }

  private t = (key: string, fallback: string) => {
    return i18n.t(key, fallback) as string;
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      const title = this.t('error.boundary_title', 'Что-то пошло не так');
      const message = this.t('error.boundary_message', 'Произошла непредвиденная ошибка. Попробуйте перезагрузить страницу.');
      const reload = this.t('error.boundary_reload', 'Перезагрузить');
      const detailsLabel = this.t('error.boundary_details', 'Детали ошибки');

      return (
        <div
          role="alert"
          aria-live="assertive"
          className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-950 px-4"
        >
          <div className="text-center p-8 max-w-md w-full bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-card">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-red-50 dark:bg-red-950/30 flex items-center justify-center text-red-600 dark:text-red-400">
                <AlertTriangle size={36} />
              </div>
            </div>

            <h2 className="text-2xl font-extrabold text-slate-900 dark:text-slate-100 mb-3 tracking-tight">
              {title}
            </h2>

            <p className="text-slate-600 dark:text-slate-400 mb-6 text-sm leading-relaxed">
              {message}
            </p>

            <button
              onClick={() => window.location.reload()}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white font-bold text-sm rounded-xl transition-all shadow-sm"
              aria-label={reload}
            >
              <RefreshCw size={18} />
              {reload}
            </button>

            {this.state.error && (
              <details className="mt-6 text-left text-sm text-slate-500 dark:text-slate-400 border-t border-slate-200 dark:border-slate-800 pt-4">
                <summary className="cursor-pointer font-medium select-none hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
                  {detailsLabel}
                </summary>
                <pre className="mt-3 p-3 bg-slate-100 dark:bg-slate-950 rounded-input text-xs overflow-auto border border-slate-200 dark:border-slate-800 font-mono text-slate-700 dark:text-slate-300">
                  {this.state.error.message}
                  {this.state.error.stack ? '\n\n' + this.state.error.stack : ''}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
